import React from "react";

import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { ROUTES } from "@utils/constant";
import { DATA_SOURCE_TYPE } from "@utils/enum";

import {
  CrimesTab,
  CrimesTabAlternative,
  DetailsTab,
  EmailsTab,
  OtherCasesTab,
  RawDataTab,
} from "./tabs";
import { useDataSourceDetailsLoader } from "./hooks";
import { useDataSourceDetailsContext } from "./providers";

export const useDataSourceDetails = () => {
  const { dataSourceDetails, emailMessages } = useDataSourceDetailsContext();
  const { isLoading } = useDataSourceDetailsLoader();
  const { formatMessage } = useIntl();
  const { case_id, data_source_id } = useParams();

  const headerConfig = {
    title: dataSourceDetails
      ? formatMessage({ id: `datasource_${dataSourceDetails?.type}` })
      : "",
  };

  const tabsContent = [
    {
      title: formatMessage({ id: "label_details" }),
      content: <DetailsTab />,
      url: ROUTES.ADMIN_SOURCE_DATA_DETAILS.URL.replace(
        ":case_id",
        case_id
      ).replace(":data_source_id", data_source_id),
    },
    {
      title: formatMessage({ id: "courts_email" }),
      content: <EmailsTab />,
      url: ROUTES.ADMIN_CASE_EMAIL_THREAD.URL.replace(
        ":case_id",
        case_id
      ).replace(":data_source_id", data_source_id),
      isHidden: emailMessages.length === 0,
    },
    {
      title: formatMessage({ id: "title_crimes" }),
      content: [
        DATA_SOURCE_TYPE.ACTAPUBLICA_V2,
        DATA_SOURCE_TYPE.ACTAPUBLICA_ROLE_ANALYSIS,
      ].includes(dataSourceDetails?.type) ? (
        <CrimesTab />
      ) : (
        <CrimesTabAlternative />
      ),
      url: ROUTES.ADMIN_CASE_CRIMES.URL.replace(":case_id", case_id).replace(
        ":data_source_id",
        data_source_id
      ),
      isHidden: ![
        DATA_SOURCE_TYPE.ACTAPUBLICA_V2,
        DATA_SOURCE_TYPE.ACTAPUBLICA_ROLE_ANALYSIS,
        DATA_SOURCE_TYPE.CHECKBIZ_PERSONINFORMATION,
        DATA_SOURCE_TYPE.VERIFERA_PERSON_INFORMATION_ROLE_ANALYSIS,
        DATA_SOURCE_TYPE.COURT_SMALL,
        DATA_SOURCE_TYPE.COURT_MEDIUM,
        DATA_SOURCE_TYPE.COURT_LARGE,
        DATA_SOURCE_TYPE.COURT,
      ].includes(dataSourceDetails?.type),
    },
    {
      title: formatMessage({ id: "title_other_cases" }),
      content: <OtherCasesTab />,
      url: ROUTES.ADMIN_CASE_OTHER_CASES.URL.replace(
        ":case_id",
        case_id
      ).replace(":data_source_id", data_source_id),
      isHidden: dataSourceDetails?.type !== DATA_SOURCE_TYPE.ACTAPUBLICA_V2,
    },
    {
      title: formatMessage({ id: "title_raw_data" }),
      content: <RawDataTab />,
      url: ROUTES.ADMIN_SOURCE_DATA_DETAILS_RAW_DATA.URL.replace(
        ":case_id",
        case_id
      ).replace(":data_source_id", data_source_id),
    },
  ];

  const breadcrumbItems = [
    {
      label: formatMessage({ id: "title_cases" }),
      url: ROUTES.ADMIN_CASES_LIST.URL,
    },
    {
      label: `${formatMessage({ id: "label_case" })} #${case_id}`,
      url: ROUTES.ADMIN_CASE_DETAILS.URL.replace(":case_id", case_id),
    },
    {
      label: formatMessage({ id: "label_source_data" }),
      url: ROUTES.ADMIN_CASE_SOURCE_DATA.URL.replace(":case_id", case_id),
    },
    {
      label: dataSourceDetails
        ? formatMessage({ id: `datasource_${dataSourceDetails?.type}` })
        : "",
    },
  ];

  return {
    headerConfig,
    tabsContent,
    isLoading,
    breadcrumbItems,
  };
};
